import { useEffect, useState } from "react";
import http from "../Utils/axiosConfig";
import downloadExcel, { milkEntryToRow, fmtNum, fmtDate, toNum, currentTenDayRange, EXPORT_LIMIT } from "../Utils/excelExport";
import MilkEntryTable from "./MilkEntryTable";
import "./MemberRegister/Details/Details.css";
import "./MemberRegister/Main/Dashboard.css";

// 20 = two shifts x the 10 days of a billing bracket, so the default period fits on
// one page and the member never sees pagination unless they widen the filter.
const PAGE_SIZE = 20;

const MilkEntryView = ({ mem_id }) => {
  // The dairy's current billing bracket, so the member sees the same period the
  // dairy will bill — not a rolling 10 days that straddles two bills.
  const period = currentTenDayRange();

  const [from, setFrom] = useState(period.from);
  const [to, setTo] = useState(period.to);
  const [showFilter, setShowFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rangeTotals, setRangeTotals] = useState({ qty: 0, amt: 0, count: 0 });
  const [dlOpen, setDlOpen] = useState(false);
  const [dlLoading, setDlLoading] = useState(false);

  const fetchRangeTotals = async (f, t) => {
    try {
      const res = await http.post("data/get_milk_entries", {
        mem_id, from_date: f, to_date: t, page: 1, limit: EXPORT_LIMIT,
      });
      if (res.code === "DATA_FOUND") {
        const all = res.data.records || [];
        setRangeTotals({
          count: res.data.total || all.length,
          qty: all.reduce((s, r) => s + Number(r.qty || 0), 0),
          amt: all.reduce((s, r) => s + Number(r.total_rate || 0), 0),
        });
      } else {
        setRangeTotals({ qty: 0, amt: 0, count: 0 });
      }
    } catch (err) {
      console.error("Error fetching range totals:", err);
    }
  };

  const fetchData = async (pageNum, f = from, t = to) => {
    setLoading(true);
    try {
      const res = await http.post("data/get_milk_entries", {
        mem_id, from_date: f, to_date: t, page: pageNum, limit: PAGE_SIZE,
      });
      if (res.code === "DATA_FOUND") {
        setRecords(res.data.records || []);
        setTotal(res.data.total || 0);
      } else {
        setRecords([]); setTotal(0);
        setRangeTotals({ qty: 0, amt: 0, count: 0 });
      }
    } catch (err) {
      console.error("Error fetching milk entries:", err);
    } finally {
      setLoading(false);
    }
  };

  // Owns every page-driven fetch, including the first load. The previous
  // `if (page !== 1)` guard meant paging back to page 1 never refetched, so the
  // table kept showing page 2's rows while the indicator read "1 / N".
  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    fetchRangeTotals(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    // Setting page to 1 only refires the effect when the value actually
    // changes, so fetch directly when already on page 1.
    if (page === 1) fetchData(1, from, to);
    else setPage(1);
    fetchRangeTotals(from, to);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const buildSummaryAoa = (recs) => ([
    ["एकूण नोंदी", recs.length],
    ["एकूण दूध (लि.)", toNum(recs.reduce((s, r) => s + Number(r.qty || 0), 0))],
    ["एकूण रक्कम (₹)", toNum(recs.reduce((s, r) => s + Number(r.total_rate || 0), 0))],
  ]);

  const doDownload = (recs) => {
    if (!recs.length) return;
    downloadExcel({
      fileName: `dudh-nondi-${from}-${to}.xlsx`,
      sheetName: "दूध नोंदी",
      rows: recs.map(milkEntryToRow),
      summaryAoa: buildSummaryAoa(recs),
      from, to,
    });
  };

  const handleDownloadPage = () => { setDlOpen(false); doDownload(records); };
  const handleDownloadAll = async () => {
    setDlLoading(true);
    try {
      const res = await http.post("data/get_milk_entries", {
        mem_id, from_date: from, to_date: to, page: 1, limit: EXPORT_LIMIT,
      });
      const all = res.code === "DATA_FOUND" ? res.data.records || [] : [];
      if (res.code === "DATA_FOUND" && (res.data.total || 0) > all.length) {
        alert(`या कालावधीत ${res.data.total} नोंदी आहेत; पहिल्या ${all.length} डाउनलोड होत आहेत. कृपया कालावधी लहान करा.`);
      }
      doDownload(all);
    } catch (err) { console.error(err); }
    finally { setDlLoading(false); setDlOpen(false); }
  };

  return (
    <div className="table-container">
      {/* Date filter is opt-in, so closed it costs one content-width pill */}
      <div className="filter-accordion">
        <button className="filter-accordion-head" onClick={() => setShowFilter((s) => !s)}
          aria-expanded={showFilter} title="कालावधी बदला">
          <span className="fa-label">📅 कालावधी</span>
          <span className="fa-value">{from && to ? `${fmtDate(from)} — ${fmtDate(to)}` : "सर्व नोंदी"}</span>
          <span className="fa-action">{showFilter ? "बंद करा ▲" : "बदला ▼"}</span>
        </button>
        <button className="download-btn" onClick={() => setDlOpen(true)}
          disabled={loading || records.length === 0} title="Excel डाउनलोड करा">
          ⬇ डाउनलोड
        </button>
      </div>

      {showFilter && (
        <div className="pr-filter-bar">
          <div className="pr-filter-group">
            <label>पासून</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="pr-filter-group">
            <label>पर्यंत</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className="pr-search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? "..." : "शोधा"}
          </button>
        </div>
      )}

      {/* Summary cards — full range, above table */}
      {!loading && rangeTotals.count > 0 && (
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-card-label">नोंदी</span>
            <span className="summary-card-value white">{rangeTotals.count}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">दूध (लि.)</span>
            <span className="summary-card-value">{fmtNum(rangeTotals.qty)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">रक्कम</span>
            <span className="summary-card-value green">₹{fmtNum(rangeTotals.amt)}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="no-data"><p>लोड होत आहे...</p></div>
      ) : (
        <>
          <MilkEntryTable records={records} />
          {totalPages > 1 && (
            <div className="pagination">
              <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>&#8249;</button>
              <span className="page-info">{page} / {totalPages}</span>
              <button className="page-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&#8250;</button>
            </div>
          )}
        </>
      )}

      {dlOpen && (
        <div className="dl-modal-overlay" onClick={() => setDlOpen(false)}>
          <div className="dl-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📥 Excel डाउनलोड करा</h3>
            <p>कोणत्या नोंदी डाउनलोड करायच्या आहेत?</p>
            <div className="dl-modal-actions">
              <button className="dl-modal-btn page" onClick={handleDownloadPage}>फक्त हे पान ({records.length} नोंदी)</button>
              <button className="dl-modal-btn all" onClick={handleDownloadAll} disabled={dlLoading}>
                {dlLoading ? "लोड होत आहे..." : `सर्व नोंदी (${total} एकूण)`}
              </button>
              <button className="dl-modal-btn cancel" onClick={() => setDlOpen(false)}>रद्द करा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilkEntryView;
